import { datadogLogs } from '@datadog/browser-logs';
import { CssBaseline, ThemeProvider } from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { LicenseInfo } from '@mui/x-license-pro';
import { ErrorBoundary } from '@sentry/nextjs';
import { AppProvider, CustomProperties, Frame } from '@shopify/polaris';
import '@shopify/polaris/build/esm/styles.css';
import enTranslations from '@shopify/polaris/locales/en.json';
import { LDFlagSet } from 'launchdarkly-node-server-sdk';
import mixpanel from 'mixpanel-browser';
import { SessionProvider } from 'next-auth/react';
import { AppProps } from 'next/app';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { ToastProvider } from 'react-toast-notifications';
import { LinkWrapper } from '../components/app/link-wrapper';
import { URLSearchParamsProvider } from '../components/hooks/useSearchParam';
import GraphqlProvider from '../components/providers/graphql';
import IdleManager from '../components/providers/idle';
import { LDFlagsContext } from '../components/providers/ld-flags';
import { setClientLoggingFlags } from '../diagnostics/client';
import { DynamicEnvironmentVariable } from '../helpers/page-helpers';
import { PagePropsBase } from '../helpers/permissions-helpers';
import '../styles/globals.scss';
import { theme } from '../styles/theme';
import styles from '../styles/theme.module.scss';
import Page500 from './500';
import MaintenanceProvider from './maintenance-provider';

LicenseInfo.setLicenseKey(process.env.NEXT_PUBLIC_MUI_LICENSE_KEY ?? '');

const SESSION_LENGTH = parseInt(
  process.env.NEXT_PUBLIC_AUTH_SESSION_LENGTH ?? '3600',
  10
);

datadogLogs.init({
  clientToken: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
  site: 'datadoghq.com',
  service: process.env.NEXT_PUBLIC_DD_SERVICE_NAME,
  forwardConsoleLogs: ['log', 'error'],
});

mixpanel.init(process.env.NEXT_PUBLIC_MIXPANEL_TOKEN, {
  debug: true,
  track_pageview: true,
  persistence: 'localStorage',
});

const SafeHydrate = ({ children }) => (
  <span suppressHydrationWarning>
    {typeof window === 'undefined' ? null : children}
  </span>
);

const TalkiatryApp = ({ Component, pageProps }: AppProps<PagePropsBase>) => {
  // Save environment variables passed in from server-side-rendering.
  if (pageProps.dynamicEnvVars) {
    DynamicEnvironmentVariable.setFromServerSideRendering(
      pageProps.dynamicEnvVars
    );
  }

  const [ldFlags, setLdFlags] = useState({ valid: false } as LDFlagSet);
  const [isLdFlagConsumer, setIsLdFlagConsumer] = useState(true);
  useEffect(() => {
    if (pageProps.ldFlags) {
      setLdFlags({ ...pageProps.ldFlags, valid: true }); // Setting to true as a fallback in case LD is down.
      setClientLoggingFlags({ ...pageProps.ldFlags, valid: true });
    } else {
      setIsLdFlagConsumer(false);
    }
  }, [pageProps.ldFlags]);

  return ldFlags.valid || !isLdFlagConsumer ? (
    <SessionProvider
      session={pageProps.session}
      refetchInterval={SESSION_LENGTH / 4}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppProvider i18n={enTranslations} linkComponent={LinkWrapper}>
          <CustomProperties className={styles.CustomPropertyOverrides}>
            <Frame>
              <URLSearchParamsProvider>
                <LDFlagsContext.Provider value={ldFlags}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <IdleManager>
                      <ToastProvider
                        autoDismiss
                        autoDismissTimeout={4000}
                        placement="bottom-center"
                      >
                        <GraphqlProvider initialState={null}>
                          <Head>
                            <title>Talkiatry</title>
                          </Head>
                          <SafeHydrate>
                            <ErrorBoundary fallback={<Page500 />}>
                              <MaintenanceProvider ldFlags={ldFlags}>
                                <Component {...pageProps} />
                              </MaintenanceProvider>
                            </ErrorBoundary>
                          </SafeHydrate>
                        </GraphqlProvider>
                      </ToastProvider>
                    </IdleManager>
                  </LocalizationProvider>
                </LDFlagsContext.Provider>
              </URLSearchParamsProvider>
            </Frame>
          </CustomProperties>
        </AppProvider>
      </ThemeProvider>
    </SessionProvider>
  ) : null;
};

export default TalkiatryApp;
